import {Helmet} from '@dr.pogodin/react-helmet';
import {closeCurrentDialog, openConfirmDialog, openDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray, noop} from 'ramda-adjunct';

import {
  usePatchTireLocationPositionDownMutation,
  usePatchTireLocationPositionUpMutation,
  usePatchTireLocationSetActiveMutation,
  usePatchTireLocationSetInactiveMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError, refreshDmsDatagrid} from '@dms/shared';

import type {ActionCallback} from 'features/eag-grid';

import {LocationForm} from './components/LocationForm';

const PREDEFINED_COLUMNS = ['name', 'isActive'] as const;

export function TireInventoryLocations() {
  const [patchTireLocationSetActive] = usePatchTireLocationSetActiveMutation();
  const [patchTireLocationSetInactive] = usePatchTireLocationSetInactiveMutation();
  const [patchTireLocationPositionUp] = usePatchTireLocationPositionUpMutation();
  const [patchTireLocationPositionDown] = usePatchTireLocationPositionDownMutation();

  const handleAddLocation = () => {
    openDialog(
      <LocationForm
        onDiscard={closeCurrentDialog}
        onSave={() => {
          closeCurrentDialog();
          refreshDmsDatagrid('tire-location');
        }}
      />,
      {
        title: i18n.t('entity.tiresInventory.labels.newLocation'),
        size: 'small',
      }
    );
  };

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    const name = getColumnData<string>('name')?.value ?? '';
    const isActive = getColumnData<boolean>('isActive')?.value ?? false;

    match(actionKey)
      .with('edit', 'redirectDetail', () =>
        openDialog(
          <LocationForm
            onDiscard={closeCurrentDialog}
            onSave={() => {
              closeCurrentDialog();
              refreshData();
            }}
            defaultValue={{
              id,
              name,
              isActive,
            }}
          />,
          {
            title: i18n.t('entity.tiresInventory.labels.editLocation'),
            size: 'small',
          }
        )
      )
      .with('setInactive', () =>
        openConfirmDialog({
          text: i18n.t('entity.tiresInventory.labels.locationSetInactive'),
          onConfirm: () =>
            patchTireLocationSetInactive({codelistId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('setActive', () =>
        openConfirmDialog({
          text: i18n.t('entity.tiresInventory.labels.locationSetActive'),
          onConfirm: () =>
            patchTireLocationSetActive({codelistId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('movePositionDown', () =>
        openConfirmDialog({
          text: i18n.t('entity.tiresInventory.labels.locationMovePositionDown'),
          onConfirm: () =>
            patchTireLocationPositionDown({codelistId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .with('movePositionUp', () =>
        openConfirmDialog({
          text: i18n.t('entity.tiresInventory.labels.locationMovePositionUp'),
          onConfirm: () =>
            patchTireLocationPositionUp({codelistId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .otherwise(noop);
  };

  return (
    <>
      <Helmet title={i18n.t('entity.tiresInventory.labels.tireLocations')} />
      <SettingsTemplate
        header={{
          title: i18n.t('entity.tiresInventory.labels.tireLocations'),
          actions: [
            {
              type: 'button',
              title: i18n.t('entity.tiresInventory.labels.newLocation'),
              onClick: handleAddLocation,
            },
          ],
        }}
        data-testid={testIds.settings.tireLocations('template')}
      >
        <DmsDataGrid
          gridCode="tire-location"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.settings.tireLocations('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
